import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Box, Container, Heading, Text, SimpleGrid, Card, 
  Avatar, Badge, VStack, HStack, Button, Spinner, Input, Image
} from '@chakra-ui/react'
import { apiConfig } from '../config/api'
import { SpotlightHeader } from '../components/Spotlight/SpotlightHeader'
import { SpotlightFooter } from '../components/Spotlight/SpotlightFooter'

interface Practitioner {
  slug: string
  name: string
  spotlight_tagline?: string
  specialties?: string[]
  profile_photo_url?: string
  location?: string
}

interface Ad {
  id: string
  coach_id: string
  creative: {
      headline: string
      body: string
      image_url?: string
      cta_url?: string
  }
}

export const DirectoryPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const searchQuery = searchParams.get('q') || ''
  
  const [practitioners, setPractitioners] = useState<Practitioner[]>([])
  const [ads, setAds] = useState<Ad[]>([])
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState(searchQuery)
  
  // Track impressions to prevent duplicate counting
  const trackedImpressions = useRef<Set<string>>(new Set())

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        // Parallel fetch
        const [dirResponse, adsResponse] = await Promise.all([
            fetch(searchQuery 
              ? `${apiConfig.endpoints.directorySearch}?q=${encodeURIComponent(searchQuery)}`
              : apiConfig.endpoints.directory),
            fetch(apiConfig.endpoints.ads.list)
        ])

        if (dirResponse.ok) {
          const data = await dirResponse.json()
          setPractitioners(data.practitioners || data.items || [])
        }
        
        if (adsResponse.ok) {
            const data = await adsResponse.json()
            setAds(data.ads || [])
        }

      } catch (error) {
        console.error("Failed to fetch data", error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [searchQuery])

  const trackAdEvent = async (type: 'impression' | 'click', campaignId: string) => {
      try {
          if (type === 'impression') {
              if (trackedImpressions.current.has(campaignId)) return;
              trackedImpressions.current.add(campaignId);
          }
          await fetch(`${apiConfig.endpoints.ads.track}?type=${type}&campaign_id=${campaignId}`, { method: 'POST' })
      } catch (e) {
          console.error("Tracking error", e)
      }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    navigate(`/directory?q=${encodeURIComponent(searchInput)}`)
  }

  const handleCardClick = (slug: string) => {
    navigate(`/${slug}`)
  }

  const handleAdClick = (ad: Ad) => {
      trackAdEvent('click', ad.id)
      if (ad.creative.cta_url) {
          window.location.href = ad.creative.cta_url
      } else {
          console.log("No CTA URL for ad", ad.id)
      }
  }

  // Interleave ads: Insert an ad every 6 items
  const getRenderedList = () => {
      const list = []
      let adIndex = 0
      
      practitioners.forEach((p, idx) => {
          list.push({ type: 'practitioner', data: p, key: p.slug })
          if ((idx + 1) % 6 === 0 && adIndex < ads.length) {
              list.push({ type: 'ad', data: ads[adIndex], key: `ad-${ads[adIndex].id}` })
              adIndex++
          }
      })
      
      // Append remaining ads if list is short? Maybe not, prevents clutter.
      return list
  }
  
  const renderedList = getRenderedList()

  return (
    <Box minH="100vh" bg="white" display="flex" flexDirection="column">
      <SpotlightHeader />
      <Box flex={1}>
        <Container maxW="7xl" py={12}>
        <VStack gap={8} align="stretch">
          {/* Header */}
          <Box textAlign="center" py={8}>
            <Heading size="2xl" mb={4} color="gray.900">
              {t('directory.title')}
            </Heading>
            <Text fontSize="md" color="gray.500" maxW="2xl" mx="auto" mb={4}>
              {t('directory.description')}
            </Text>
            <Text fontSize="sm" color="gray.400" maxW="2xl" mx="auto">
              {t('directory.learn_more_link')} <a href="/" style={{ color: '#1f7a52', textDecoration: 'underline' }}>{t('directory.learn_more_text')}</a>
            </Text>
          </Box>

          {/* Search */}
          <Box as="form" onSubmit={handleSearch}>
            <HStack gap={2}>
              <Input
                placeholder={t('directory.search_placeholder')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                size="lg"
                flex={1}
              />
              <Button type="submit" colorPalette="blue" size="lg">
                {t('common.search')}
              </Button>
            </HStack>
          </Box>

          {/* Results */}
          {loading ? (
            <Box textAlign="center" py={12}>
              <Spinner size="xl" color="brand.500" />
            </Box>
          ) : practitioners.length === 0 ? (
            <Box textAlign="center" py={12}>
              <Text fontSize="lg" color="fg.muted">
                {searchQuery ? t('directory.no_results_description') : t('directory.no_results')}
              </Text>
            </Box>
          ) : (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
              {renderedList.map((item) => {
                  if (item.type === 'practitioner') {
                    const practitioner = item.data as Practitioner
                    return (
                        <Card.Root
                        key={practitioner.slug}
                        cursor="pointer"
                        onClick={() => handleCardClick(practitioner.slug)}
                        _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                        transition="all 0.2s"
                        >
                        <Card.Body>
                            <VStack gap={4} align="stretch">
                            <HStack gap={4}>
                                <Avatar.Root size="lg">
                                <Avatar.Image src={practitioner.profile_photo_url} />
                                <Avatar.Fallback name={practitioner.name} />
                                </Avatar.Root>
                                <VStack align="start" gap={1} flex={1}>
                                <Heading size="md">{practitioner.name}</Heading>
                                {practitioner.location && (
                                    <Text fontSize="sm" color="fg.muted">
                                    {practitioner.location}
                                    </Text>
                                )}
                                </VStack>
                            </HStack>
                            
                            {practitioner.spotlight_tagline && (
                                <Text fontSize="sm" color="fg.muted" noOfLines={2}>
                                {practitioner.spotlight_tagline}
                                </Text>
                            )}
                            
                            {practitioner.specialties && practitioner.specialties.length > 0 && (
                                <HStack gap={2} wrap="wrap">
                                {practitioner.specialties.slice(0, 3).map((specialty, idx) => (
                                    <Badge key={idx} colorPalette="blue" variant="subtle" fontSize="xs">
                                    {specialty}
                                    </Badge>
                                ))}
                                </HStack>
                            )}
                            
                            <Button
                                w="full"
                                colorPalette="blue"
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                e.stopPropagation()
                                handleCardClick(practitioner.slug)
                                }}
                            >
                                {t('directory.view_profile')}
                            </Button>
                            </VStack>
                        </Card.Body>
                        </Card.Root>
                    )
                  } else {
                      const ad = item.data as Ad
                      return (
                          <Card.Root
                            key={item.key}
                            cursor="pointer"
                            onClick={() => handleAdClick(ad)}
                            _hover={{ shadow: 'lg', transform: 'translateY(-2px)' }}
                            transition="all 0.2s"
                            borderColor="yellow.400"
                            borderWidth="2px"
                            onMouseEnter={() => trackAdEvent('impression', ad.id)}
                          >
                             <Card.Body bg="yellow.50">
                                 <VStack gap={4} align="stretch">
                                     <HStack justify="space-between">
                                         <Badge colorPalette="yellow" variant="solid">Sponsored</Badge>
                                     </HStack>
                                     
                                     {ad.creative.image_url && (
                                         <Image src={ad.creative.image_url} alt="Ad" borderRadius="md" objectFit="cover" maxH="150px"/>
                                     )}

                                     <Heading size="md">{ad.creative.headline}</Heading>
                                     <Text fontSize="sm" color="fg.muted" noOfLines={3}>
                                         {ad.creative.body}
                                     </Text>
                                     
                                     <Button
                                        w="full"
                                        colorPalette="yellow"
                                        size="sm"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleAdClick(ad)
                                        }}
                                     >
                                         Learn More
                                     </Button>
                                 </VStack>
                             </Card.Body>
                          </Card.Root>
                      )
                  }
              })}
            </SimpleGrid>
          )}
        </VStack>
        </Container>
      </Box>
      <SpotlightFooter />
    </Box>
  )
}
